import axios from 'axios';
import { RootStore } from '../../state/RootStore';
import { Cart } from '@common/merch';

interface AddToCartRequest {
  cartId: string;
  merchandiseId: string;
  quantity: number;
}

export class CartController {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  public async ensureCart(): Promise<string | null> {
    let cartId = localStorage.getItem('merch_cart_id');

    if (!cartId) {
      try {
        const response = await axios.post('/api/merch/cart');

        if (response.data.success && response.data.data) {
          const newCartId = response.data.data.id;
          localStorage.setItem('merch_cart_id', newCartId);
          this.rootStore.cartStore.setCart(response.data.data);
          return newCartId;
        } else {
          this.rootStore.alertStore.showAlert('Failed to create cart', 'error');
          return null;
        }
      } catch (error) {
        console.error('Error creating cart:', error);
        this.rootStore.alertStore.showAlert('Failed to create cart', 'error');
        return null;
      }
    }

    return cartId;
  }

  public async addToCart(merchandiseId: string, quantity: number = 1): Promise<boolean> {
    const cartId = await this.ensureCart();
    if (!cartId) return false;

    this.rootStore.cartStore.setLoading(true, merchandiseId, 'add');

    try {
      const requestBody: AddToCartRequest = {
        cartId,
        merchandiseId,
        quantity
      };

      const response = await axios.post('/api/merch/cart/add', requestBody);

      if (response.data.success) {
        this.rootStore.cartStore.setCart(response.data.data);
        return true;
      }

      return false;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorMessage = error.response.data?.error || 'Failed to add to cart';
        this.rootStore.alertStore.showAlert(errorMessage, 'error');
      } else {
        this.rootStore.alertStore.showAlert('Failed to add to cart', 'error');
      }
      console.error('Error adding to cart:', error);
      return false;
    } finally {
      this.rootStore.cartStore.setLoading(false);
    }
  }

  public async removeFromCart(lineId: string): Promise<boolean> {
    const cartId = localStorage.getItem('merch_cart_id');
    if (!cartId) {
      this.rootStore.alertStore.showAlert('Cart not found', 'error');
      return false;
    }

    this.rootStore.cartStore.setLoading(true, lineId, 'remove');

    try {
      const response = await axios.post('/api/merch/cart/remove', {
        cartId,
        lineId
      });

      if (response.data.success) {
        this.rootStore.cartStore.setCart(response.data.data);
        return true;
      }

      return false;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorMessage = error.response.data?.error || 'Failed to remove from cart';
        this.rootStore.alertStore.showAlert(errorMessage, 'error');
      } else {
        this.rootStore.alertStore.showAlert('Failed to remove from cart', 'error');
      }
      console.error('Error removing from cart:', error);
      return false;
    } finally {
      this.rootStore.cartStore.setLoading(false);
    }
  }

  public async updateQuantity(lineId: string, quantity: number): Promise<boolean> {
    const cartId = localStorage.getItem('merch_cart_id');
    if (!cartId) {
      this.rootStore.alertStore.showAlert('Cart not found', 'error');
      return false;
    }

    this.rootStore.cartStore.setLoading(true, lineId, 'update');

    try {
      const response = await axios.put('/api/merch/cart/update', {
        cartId,
        lineId,
        quantity
      });

      if (response.data.success) {
        this.rootStore.cartStore.setCart(response.data.data);
        return true;
      }

      return false;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const errorMessage = error.response.data?.error || 'Failed to update quantity';
        this.rootStore.alertStore.showAlert(errorMessage, 'error');
      } else {
        this.rootStore.alertStore.showAlert('Failed to update quantity', 'error');
      }
      return false;
    } finally {
      this.rootStore.cartStore.setLoading(false);
    }
  }

  public async fetchCart(): Promise<void> {
    const cartId = localStorage.getItem('merch_cart_id');
    if (!cartId) return;

    try {
      const response = await axios.get('/api/merch/cart/fetch', {
        params: { cartId }
      });

      if (response.data.success && response.data.data) {
        const cart: Cart = response.data.data;
        this.rootStore.cartStore.setCart(cart);
      } else {
        // Stored cart no longer exists on Fourthwall (expired or checked out) — drop it.
        this.discardStaleCart();
      }
    } catch (error) {
      // fetchCart runs automatically on load, so don't nag the user. A 4xx means the
      // stored cart id is stale/invalid — clear it silently so a fresh cart is created
      // on the next add. Keep it on 5xx/network errors so a transient outage doesn't
      // wipe a still-valid cart.
      if (axios.isAxiosError(error) && error.response && error.response.status < 500) {
        this.discardStaleCart();
      }
      console.error('Error fetching cart:', error);
    }
  }

  private discardStaleCart(): void {
    localStorage.removeItem('merch_cart_id');
    this.rootStore.cartStore.clearCart();
  }

  public clearCart(): void {
    localStorage.removeItem('merch_cart_id');
    localStorage.removeItem('merch_currency');
    this.rootStore.cartStore.clearCart();
  }

  public async handleCheckout(): Promise<void> {
    const checkoutUrl = this.rootStore.cartStore.checkoutUrl;

    if (!checkoutUrl) {
      this.rootStore.alertStore.showAlert('No checkout URL found', 'error');
      return;
    }

    window.location.href = checkoutUrl;
  }
}
